import { FlexContainer } from '@bos/ui';
import { useMatches } from '@tanstack/react-router';
import { type ReactNode } from 'react';

import {
	useRedirectToLoginPage,
	useTokenContext,
	useUserScopes,
} from '../../context/TokenProvider/TokenProvider';
import { useLogout } from '../../helpers/token/useLogout';
import { useI18nContext } from '../../locale/i18n-react';
import { homeRoute } from '../../pages/HomePage/route';
import { AppSpinner } from '../AppSpinner';
import { AppText } from '../AppText/AppText';

import { InvalidTokensDialog } from './InvalidTokensDialog';
import { useHasValidTokens } from './hooks/useHasValidTokens';
import { useRedirectToStoredUrlAfterAuth } from './hooks/useRedirectAfterAuth';

export type PidAuthGuardProps = {
	children: ReactNode;
};

export const PidAuthGuard = ({ children }: PidAuthGuardProps) => {
	const { LL } = useI18nContext();

	const {
		hasValidAccessToken,
		hasValidRefreshToken,
		isRefreshingTokens,
		isLoginRedirectLoading,
		isLoginRedirectAvailable,
	} = useTokenContext();

	const { hasValidTokens } = useHasValidTokens();

	const redirectToMSLoginPage = useRedirectToLoginPage();

	const {
		userScopes,
		isLoading: isUserScopesLoading,
		isError: isUserScopesError,
	} = useUserScopes();
	const hasUserScopes = !!userScopes;

	const matches = useMatches();
	const logout = useLogout();

	const maybeHomeRoute = matches.find(
		(match) => match.routeId === homeRoute.id,
	);

	const shouldRedirectToMicrosoftLogin =
		!!maybeHomeRoute?.search.shouldRedirectToMicrosoftLogin;

	const shouldRedirect =
		!hasValidAccessToken &&
		isLoginRedirectAvailable &&
		shouldRedirectToMicrosoftLogin;

	const isHomeRoute = !!maybeHomeRoute;

	useRedirectToStoredUrlAfterAuth({
		hasValidTokens,
	});

	if (hasValidTokens) {
		return children;
	}

	if (shouldRedirect) {
		redirectToMSLoginPage();
		return undefined;
	}

	if (isHomeRoute) {
		logout(false);
		return undefined;
	}

	const isLoggingIn = shouldRedirect || isLoginRedirectLoading;
	const waitingForTokens = !hasValidAccessToken && isLoggingIn;
	const waitingForUserScopes = !hasUserScopes && isUserScopesLoading;
	const waiting = waitingForTokens || waitingForUserScopes;
	if (waiting) {
		return (
			<FlexContainer spacing center fullHeight>
				<AppSpinner />
			</FlexContainer>
		);
	}

	const isNotAbleToLogin =
		!hasValidRefreshToken &&
		!isLoginRedirectLoading &&
		!isLoginRedirectAvailable;
	const isAuthenticationError =
		!hasValidAccessToken && isNotAbleToLogin && !isRefreshingTokens;
	const isNotAbleToLoadUserScopes =
		!hasUserScopes && !isUserScopesLoading && isUserScopesError;
	const isAuthorizationError =
		!isAuthenticationError && isNotAbleToLoadUserScopes;
	const isError = isAuthenticationError || isAuthorizationError;
	if (isError) {
		const authErrorText = LL.authGuard.dataNotAccessible();
		return <AppText>{authErrorText}</AppText>;
	}

	return <InvalidTokensDialog />;
};
